import { useCallback, useMemo, useRef, useState } from "react";
import { Stack, Popper, Grow, Paper, ClickAwayListener, MenuList, MenuItem } from "@mui/material";

import PrimaryButton from "./primary_button";

/**
 * PopperButton
 */

type Props = {
  buttonLabel: string,
  buttonColor?: "primary-green" | "white" | "secondary" | "secondary-white" | "yellow" | "red",
  options: { id: number, label: string, visibility?: boolean }[],
  onSelect: (idOption: number) => void,
};

export const PopperButton = ({
  buttonLabel,
  buttonColor,
  options,
  onSelect,
}: Props) => {
  const menuRef = useRef(null);

  const [openMenu, setOpenMenu] = useState(false);

  const visibleOptions = useMemo(() => {
    return options.filter(x => x.visibility === true || x.visibility === undefined);
  }, [
    options,
  ]);

  const handleSelect = useCallback((idOption: number) => {
    setOpenMenu(prev => !prev);
    onSelect(idOption);
  }, [
    onSelect
  ]);

  return (
    <Stack direction="row" spacing={2}>
      <div className="w-[12rem]">
        <PrimaryButton
          ref={menuRef}
          id="composition-button"
          aria-controls={openMenu ? 'composition-menu' : undefined}
          aria-expanded={openMenu ? 'true' : undefined}
          aria-haspopup="true"
          onClick={() => setOpenMenu(prev => !prev)}
          buttonColor={buttonColor}>
          {buttonLabel}
        </PrimaryButton>
        <Popper
          style={{ zIndex: 10000 }}
          open={openMenu}
          anchorEl={menuRef.current}
          role={undefined}
          placement="bottom-start"
          transition
          disablePortal>
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{ transformOrigin: placement === 'bottom-start' ? 'left top' : 'left bottom' }}>
              <Paper>
                <ClickAwayListener onClickAway={() => setOpenMenu(false)}>
                  <MenuList
                    autoFocusItem={openMenu}
                    id="composition-menu"
                    aria-labelledby="composition-button"
                    onKeyDown={(event) => {
                      if (event.key === 'Tab') {
                        event.preventDefault();
                        setOpenMenu(false);
                      } else if (event.key === 'Escape') {
                        setOpenMenu(false);
                      }
                    }}>
                    {
                      visibleOptions.map(opt => (
                        <MenuItem
                          key={opt.id}
                          onClick={() => handleSelect(opt.id)}>
                          {opt.label}
                        </MenuItem>
                      ))
                    }
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </div>
    </Stack>
  );
}